import React, { useEffect, useMemo } from 'react';
import { Box, FormControl, FormLabel, Stack } from '@mui/material';
import { FormComponentBuilder } from 'components/common/form/layout';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormElementProps } from 'components/common/form/index';
import ReactSelect from 'components/common/react-select/ReactSelect';
import useBundleTranslation from 'i18n';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { ALL_TYPES, resourceSearchFilter, useResourceTypeFilter, withShortLabelDesc } from './useResourceTypeFilter';
import { ResourceMultiValue, ResourceOption } from './resourceOptionParts';

interface InsightResourcePickerProps extends FormControlProps {
    data: Array<FormComponentValue>;
    elementProps: FormElementProps;
    // Name of the sibling single-select Primary field on the same form.
    primaryFieldName?: string;
    // Live value of the primary field, injected via a `deps` rule on PHP-driven forms.
    livePrimaryValue?: string;
}

export default function InsightResourcePicker({
    controlProps,
    elementProps,
}: FormElementControlPropsType<InsightResourcePickerProps>) {
    const { t } = useBundleTranslation('app/editor/element/extreport');
    const rawValue: string[] = useMemo(() => {
        if (Array.isArray(controlProps.value)) return controlProps.value.map(String);
        if (controlProps.value) return [String(controlProps.value)];
        return [];
    }, [controlProps.value]);

    // The primary is a separate, sibling field — read live so a resource just promoted to
    // primary (not yet saved) can neither be re-picked nor stay picked here.
    const primaryFieldName = controlProps.primaryFieldName ?? 'primary_resource';
    const primaryValue: string =
        controlProps.livePrimaryValue !== undefined
            ? String(controlProps.livePrimaryValue)
            : (elementProps?.form?.hookFormWatch(primaryFieldName) ?? '');

    const value = useMemo(() => rawValue.filter((v) => v !== primaryValue), [rawValue, primaryValue]);

    // The primary is a separate field/control now — controlProps.data only ever carries
    // Additional candidates (backend already excludes the chosen primary from this list).
    const options = useMemo(
        () => withShortLabelDesc(controlProps.data.filter((opt) => String(opt.value) !== primaryValue)),
        [controlProps.data, primaryValue],
    );

    const { typeFilter, setTypeFilter, typeFilterOptions, filteredOptions } = useResourceTypeFilter(options, value);

    const handleChange = (newVal: string | string[]) => {
        controlProps.onChange(Array.isArray(newVal) ? newVal : newVal ? [newVal] : []);
    };

    // A live promotion to primary must drop the resource from the stored Additional value too,
    // not just hide it — otherwise it silently reappears the moment the primary changes again.
    useEffect(() => {
        if (primaryValue && rawValue.includes(primaryValue)) {
            controlProps.onChange(rawValue.filter((v) => v !== primaryValue));
        }
    }, [primaryValue]);

    if (options.length === 0) {
        return null;
    }

    return (
        <Stack>
            <FormControl>
                <FormLabel>{t('concierge_insight_additional_chip')}</FormLabel>
                <Stack direction="row" sx={{ mb: 2 }}>
                    <StaticAddon>{t('concierge_insight_filter_by_type')}</StaticAddon>
                    <Box sx={{ flexGrow: 1 }}>
                        <ReactSelect
                            data={typeFilterOptions}
                            value={typeFilter}
                            update={(newVal) => setTypeFilter((newVal as string) || ALL_TYPES)}
                            name={`${controlProps.name}_type_filter`}
                            isMulti={false}
                            isClearable={false}
                            disabled={controlProps.readOnly}
                            dataTest={`${controlProps.name}_type_filter`}
                        />
                    </Box>
                </Stack>
                <ReactSelect
                    data={filteredOptions}
                    value={value}
                    update={handleChange}
                    name={controlProps.name}
                    isMulti={true}
                    keepValueOrder={true}
                    disabled={controlProps.readOnly}
                    dataTest={controlProps.name}
                    placeholder={t('concierge_insight_resource_search_ph')}
                    filterOption={resourceSearchFilter}
                    noOptionsMessage={() => t('concierge_insight_resource_search_empty')}
                    optionComponent={ResourceOption as any}
                    overrideComponents={{ MultiValue: ResourceMultiValue }}
                />
            </FormControl>
        </Stack>
    );
}

export class InsightResourcePickerBuilder extends FormComponentBuilder {
    // An emptied picker posts the bare key with '' (as TransferList does): the save keeps the stored rows when the key is absent (MI-30707).
    processFormData(formData: FormData, key: string, data: any) {
        if (!Array.isArray(data)) {
            formData.append(key, data);
        } else if (data.length === 0) {
            formData.append(key, '');
        } else {
            data.forEach((v: string) => formData.append(key + '[]', v));
        }
    }

    prepareProps(): InsightResourcePickerProps {
        return {
            ...this.controlProps,
            data: this.componentValues,
            elementProps: this.elementProps,
            primaryFieldName: this.elementProps.componentProps?.primaryFieldName,
            livePrimaryValue: this.elementProps.componentProps?.livePrimaryValue,
        };
    }
}
